import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import OverviewTab from "../components/payrollFinancing/OverviewTab";
import WithdrawalsTab from "../components/payrollFinancing/WithdrawalsTab";
import TransfersTab from "../components/payrollFinancing/TransfersTab";
import RepaymentsTab from "../components/payrollFinancing/RepaymentsTab";
import UseOfFundsTab from "../components/payrollFinancing/UseOfFundsTab";
import StatementTab from "../components/payrollFinancing/StatementTab";
import { PortalSelect, todayIso } from "../components/payrollFinancing/shared";
import { buildPosition } from "../services/payrollFinanceCalculator";
import { canUsePayrollFinancing, loadAll } from "../services/payrollFinanceService";

const TABS = [
  ["OVERVIEW", "Overview"], ["WITHDRAWALS", "Withdrawals"], ["TRANSFERS", "Transfers & Charges"],
  ["REPAYMENTS", "Repayments"], ["USE", "Use of Funds"], ["STATEMENT", "Statement"]
];

export default function PayrollFinancing() {
  const [tab, setTab] = useState("OVERVIEW");
  const [portal, setPortal] = useState("ALL");
  const [asOf, setAsOf] = useState(todayIso());
  const [data, setData] = useState({ employees: [], withdrawals: [], movements: [], repayments: [], uses: [] });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    try { setData(await loadAll()); setError(""); }
    catch (err) { setError(err.message || "Could not load payroll financing records."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const notify = (text, isError = false) => {
    if (isError) { setError(text); setMessage(""); } else { setMessage(text); setError(""); }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const position = useMemo(() => buildPosition({
    withdrawals: data.withdrawals, movements: data.movements, repayments: data.repayments, uses: data.uses, asOf, portal
  }), [data, asOf, portal]);

  if (!canUsePayrollFinancing()) {
    return (
      <div className="app-shell"><Sidebar />
        <main className="main-content"><div className="management-alert error">You do not have access to this page.</div></main>
      </div>
    );
  }

  const shared = { data, position, portal, asOf, reload, notify };

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management Finance</div>
            <h1 className="page-title">Payroll Financing</h1>
            <p className="page-copy">
              Salary advances funded by the partner bank: what was withdrawn, which employee account it went to, what came back to ALM,
              what it was used for, and what is owed to the bank — 1.9% a month, charged per day on what is outstanding.
            </p>
          </div>
          <div className="pnl-print-hide" style={{ display: "flex", gap: 12 }}>
            <PortalSelect value={portal} onChange={setPortal} allowAll label="Portal" />
            <div className="field" style={{ minWidth: 170 }}>
              <label>Interest as of</label>
              <input className="input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value || todayIso())} />
            </div>
          </div>
        </header>

        {(message || error) && <div className={`management-alert pnl-print-hide ${error ? "error" : "success"}`}>{error || message}</div>}

        <div className="button-row pnl-print-hide" style={{ marginBottom: 16, flexWrap: "wrap" }}>
          {TABS.map(([key, label]) => (
            <button key={key} type="button" className={`btn ${tab === key ? "btn-primary" : "btn-muted"}`} onClick={() => setTab(key)}>{label}</button>
          ))}
          {loading && <span className="page-copy" style={{ alignSelf: "center" }}>Loading…</span>}
        </div>

        {tab === "OVERVIEW" && <OverviewTab {...shared} />}
        {tab === "WITHDRAWALS" && <WithdrawalsTab {...shared} />}
        {tab === "TRANSFERS" && <TransfersTab {...shared} />}
        {tab === "REPAYMENTS" && <RepaymentsTab {...shared} />}
        {tab === "USE" && <UseOfFundsTab {...shared} />}
        {tab === "STATEMENT" && <StatementTab {...shared} />}
      </main>
    </div>
  );
}
